import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Typography,
  Button,
  Box,
  Dialog,
  DialogContent,
  DialogActions,} from '@mui/material';
import { Add, ErrorOutlineRounded } from '@mui/icons-material';
import { FormListProps } from '../../types';
import { PaginationBar } from '../shared/PaginationBar';
import { Toast } from '../shared/Toast';
import { ShareFormDialog } from './ShareFormDialog';
import { FormListSkeleton } from './FormListSkeleton';
import { FormListToolbar } from './FormListToolbar';
import { FormListRow } from './FormListRow';
import { FormListEmpty } from './FormListEmpty';
import { FormRowMenu } from './FormRowMenu';
import { useForms, FORMS_PAGE_SIZE } from './useForms';
import { useDeleteForm } from './useDeleteForm';
import { useNarrowRow } from './useNarrowRow';
import { copyToClipboard, formPaths, publicFormUrl } from './utils';
import { CreateFormDialog } from '../../pages/form-wizard';
import { DesktopOnlyNotice } from '../shared/DesktopOnlyNotice';
import { useIsMobileViewport } from '../../hooks/useIsMobileViewport';
import './FormList.css';

export const FormList: React.FC<FormListProps> = ({ tenantId }) => {
  const paths = formPaths(tenantId);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobileViewport = useIsMobileViewport();
  const [createOpen, setCreateOpen] = React.useState(
    searchParams.get('create') === '1' && !isMobileViewport,
  );
  const [desktopNoticeOpen, setDesktopNoticeOpen] = React.useState(
    searchParams.get('create') === '1' && isMobileViewport,
  );

  const openCreateDialog = () => {
    if (isMobileViewport) {
      setDesktopNoticeOpen(true);
      return;
    }
    setCreateOpen(true);
  };
  const closeCreateDialog = () => {
    setCreateOpen(false);
    if (searchParams.has('create')) {
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  };
  const closeDesktopNotice = () => {
    setDesktopNoticeOpen(false);
    if (searchParams.has('create')) {
      searchParams.delete('create');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const {
    forms,
    totalCount,
    loading,
    initialLoading,
    page,
    handlePageChange,
    removeFormLocally,
    refetch,
    search,
    setSearch,
    type,
    setType,
    sort,
    setSort,
    isFiltered,
    clearFilters,
  } = useForms(tenantId);
  const {
    deleteDialogOpen,
    deleting,
    handleDeleteClick,
    handleDeleteCancel,
    handleDeleteConfirm,
  } = useDeleteForm(tenantId, removeFormLocally);

  const { ref: pageRef } = useNarrowRow();

  const [rowMenu, setRowMenu] = React.useState<{
    anchor: HTMLElement;
    formId: string;
  } | null>(null);
  const [shareForm, setShareForm] = React.useState<{
    id: string;
    name: string;
  } | null>(null);
  const [copied, setCopied] = React.useState(false);

  const menuForm = rowMenu
    ? forms.find((form) => form.id === rowMenu.formId) ?? null
    : null;

  const closeRowMenu = () => setRowMenu(null);

  const openShare = (id: string, name?: string) =>
    setShareForm({ id, name: name ?? '' });

  const handleCopyLink = async () => {
    if (menuForm && (await copyToClipboard(publicFormUrl(tenantId, menuForm.id)))) {
      setCopied(true);
    }
    closeRowMenu();
  };

  const handleEdit = (editPath: string) => {
    if (isMobileViewport) {
      setDesktopNoticeOpen(true);
      return;
    }
    navigate(editPath);
  };

  if (initialLoading) {
    return <FormListSkeleton />;
  }

  return (
    <Box className="admin-page form-list-page" ref={pageRef}>
      <Box className="form-list-top">
        <Box className="form-list-header">
          <Box>
            <Typography className="admin-page-title">Forms</Typography>
            <Typography className="admin-page-subtitle">
              Create, manage, and track responses for your forms
            </Typography>
          </Box>
          <Button
            className="admin-primary-btn"
            variant="contained"
            disableElevation
            startIcon={<Add sx={{ fontSize: 18 }} />}
            onClick={openCreateDialog}
          >
            Create New Form
          </Button>
        </Box>

        {(forms.length > 0 || isFiltered) && (
          <FormListToolbar
            search={search}
            onSearchChange={setSearch}
            type={type}
            onTypeChange={setType}
            sort={sort}
            onSortChange={setSort}
            onRefresh={refetch}
          />
        )}
      </Box>

      <Box className="form-list-scroll">
        {forms.length === 0 ? (
          <FormListEmpty
            isFiltered={isFiltered}
            onClearFilters={clearFilters}
            onCreate={openCreateDialog}
          />
        ) : (
          <Box className="form-list-rows" sx={{ opacity: loading ? 0.6 : 1, transition: 'opacity 120ms ease' }}>
            {forms.map((form) => (
              <FormListRow
                key={form.id}
                form={form}
                editPath={paths.edit(form.id)}
                responsesPath={paths.responses(form.id)}
                onPreview={() => openShare(form.id, form.name)}
                onOpenMenu={(anchor) => setRowMenu({ anchor, formId: form.id })}
                onEdit={handleEdit}
              />
            ))}
          </Box>
        )}
      </Box>

      <FormRowMenu
        anchor={rowMenu?.anchor ?? null}
        open={Boolean(rowMenu)}
        viewUrl={menuForm ? publicFormUrl(tenantId, menuForm.id) : '#'}
        onClose={closeRowMenu}
        onCopyLink={handleCopyLink}
        onDelete={() => {
          if (menuForm) handleDeleteClick(menuForm.id);
          closeRowMenu();
        }}
      />

      {forms.length > 0 && (
        <Box className="form-list-footer">
          <PaginationBar
            page={page}
            count={Math.max(1, Math.ceil(totalCount / FORMS_PAGE_SIZE))}
            onChange={handlePageChange}
            disabled={totalCount <= FORMS_PAGE_SIZE}
          />
        </Box>
      )}

      <ShareFormDialog
        open={Boolean(shareForm)}
        formName={shareForm?.name ?? ''}
        shareUrl={shareForm ? publicFormUrl(tenantId, shareForm.id) : ''}
        onClose={() => setShareForm(null)}
      />

      <Toast
        open={copied}
        message="Link copied to clipboard"
        severity="success"
        onClose={() => setCopied(false)}
      />

      <Dialog
        className="form-list-delete-dialog"
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        maxWidth="xs"
        fullWidth
      >
        <DialogContent sx={{ p: 3, pb: 2 }}>
          <Box className="form-list-delete-icon">
            <ErrorOutlineRounded
              sx={{ fontSize: 26, color: 'var(--admin-danger)' }}
            />
          </Box>
          <Typography className="form-list-delete-title">
            Delete this form?
          </Typography>
          <Typography className="form-list-delete-subtitle">
            This action cannot be undone and all associated responses will be
            permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions className="form-list-delete-actions">
          <Button className="admin-dialog-btn" onClick={handleDeleteCancel} fullWidth>
            Cancel
          </Button>
          <Button
            className="admin-dialog-btn"
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disableElevation
            fullWidth
            disabled={deleting}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <CreateFormDialog
        open={createOpen}
        tenantId={tenantId}
        onClose={closeCreateDialog}
      />

      <DesktopOnlyNotice open={desktopNoticeOpen} onClose={closeDesktopNotice} />
    </Box>
  );
};
